import { AsyncPipe, LowerCasePipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService, translate } from '@jsverse/transloco';
import {
  AlertComponent,
  CSVExportService,
  CardComponent,
  DisabledGranularity,
  FilterPipe,
  FormatDatePipe,
  FormatDatetimeTimezonePipe,
  FormatNumberPipe,
  FormatterService,
  MessengerService,
  Period,
  PeriodDisplayComponent,
  PeriodGranularityModeComponent,
  PeriodPickerComponent,
  PeriodPrevNextComponent,
  PeriodService,
  RoutingService,
  SelectSearchBarComponent,
  Str,
  TimeShortcut,
  TimeUnit,
  __,
  blueZodiac,
  cantaloupe,
  cornflower,
  green,
  orange,
  pastelGreen,
  purple,
  randomString,
  red,
} from '@web/birdz-angular';
import { KpiFavoriteSaveComponent } from 'app/components/kpi-favorite-save/kpi-favorite-save.component';
import { KpiFavoritesMenuComponent } from 'app/components/kpi-favorites-menu/kpi-favorites-menu.component';
import { ModalHelpContractConfigComponent } from 'app/components/modal-help-contract-config/modal-help-contract-config.component';
import { ModalHelpSewerinDataReportingComponent } from 'app/components/modal-help-sewerin-data-reporting/modal-help-sewerin-data-reporting.component';
import { HcAxisInternal } from 'app/interfaces/hc-axis-internal.interface';
import { EXTRA_KPIS, KpiOverviewItem } from 'app/interfaces/kpi-overview-item.interface';
import { KpiRateHistoryPoint } from 'app/interfaces/kpi-rate-history-point.interface';
import { KpiRateHistory } from 'app/interfaces/kpi-rate-history.interface';
import { Kpi, isKpiDisplayableForGranularity } from 'app/interfaces/kpi.interface';
import { Api } from 'app/services/api.service';
import { FilterService } from 'app/services/filter.service';
import { KpiFavoritesService } from 'app/services/kpi-favorites.service';
import { UserService } from 'app/services/user.service';
import { KpiType } from 'app/types/kpi-type.type';
import chroma from 'chroma-js';
import {
  Locale,
  differenceInCalendarDays,
  endOfISOWeek,
  endOfMonth,
  format,
  max,
  parseISO,
  startOfISOWeek,
  startOfMonth,
  startOfYesterday,
  sub,
} from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import {
  EMPTY,
  Observable,
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs';
import { fontFamily } from '../../config/constants.config';
import { ModalHelpIndexReportingComponent } from '../modal-help-index-reporting/modal-help-index-reporting.component';

// colors of the daily steps of YTD kpis (columns), in display order
const DAILY_STEP_COLORS = [cornflower, pastelGreen, cantaloupe, purple];

@Component({
  selector: 'app-card-kpi-summary',
  templateUrl: './card-kpi-summary.component.html',
  styleUrls: ['./card-kpi-summary.component.scss'],
  providers: [
    PeriodService,
    // shared by the save star and the favorites menu, which sit at two different places in the toolbar
    KpiFavoritesService,
    {
      provide: 'initialPeriodState',
      useFactory: () => {
        const yesterday = startOfYesterday();
        const thirtyDaysBefore = sub(yesterday, { days: 29 });
        const threeYearsAgo = sub(new Date(), { years: 3 });
        return {
          period: { from: thirtyDaysBefore, to: yesterday },
          min: max([threeYearsAgo, new Date(2025, 0, 1, 0, 0, 0)]),
          // les données de la journée en cours ne sont pas encore synchronisées
          max: yesterday,
          snapTo: null,
          granularity: 'day',
        };
      },
    },
  ],
  imports: [
    CardComponent,
    AsyncPipe,
    MatButtonModule,
    MatIconModule,
    PeriodPickerComponent,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    MatSlideToggleModule,
    MatOptionModule,
    MatTooltipModule,
    HighchartsChartComponent,
    PeriodPrevNextComponent,
    TranslocoPipe,
    FormatNumberPipe,
    LowerCasePipe,
    FormatDatePipe,
    FormatDatetimeTimezonePipe,
    FilterPipe,
    PeriodDisplayComponent,
    SelectSearchBarComponent,
    PeriodGranularityModeComponent,
    AlertComponent,
    AsyncPipe,
    KpiFavoriteSaveComponent,
    KpiFavoritesMenuComponent,
  ],
})
export class CardKpiSummaryComponent implements OnInit {
  private filterService = inject(FilterService);
  private api = inject(Api);
  private messenger = inject(MessengerService);
  private periodService = inject(PeriodService);
  private userService = inject(UserService);
  private transloco = inject(TranslocoService);
  private formatter = inject(FormatterService);
  private dialog = inject(MatDialog);
  private routing = inject(RoutingService);
  private csvExport = inject(CSVExportService);
  private favoritesService = inject(KpiFavoritesService);
  private ref = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  hasPerimeter$: Observable<boolean>;
  techno$: Observable<string | null>;
  isLoading = 0;
  kpis: Kpi[] = [];
  // kept in sync with `kpis`, the ids of the KPIs the current contract offers
  availableKpiIds: string[] = [];
  kpiSearchFunction: (search: string, option: Kpi) => boolean = (search, option) => Str.search(search, option.label);
  // the initializer has to stay on the backing field : assigning `selectedKpis` here would run
  // the setter during field initialization, before the injected services are available
  private _selectedKpis: string[] = [];
  get selectedKpis(): string[] {
    return this._selectedKpis;
  }

  /**
   * Every assignment reports the selection to the favorites, which can't watch it on their own :
   * the select's search bar mutates the array in place when a KPI is checked instead of assigning
   * a new one (see SelectSearchBarComponent.onMultipleSelectSelectionChange), so no reference
   * comparison would catch it. It reassigns `matSelect.value` though, which ngModel turns back
   * into an assignment here.
   */
  set selectedKpis(kpis: string[]) {
    this._selectedKpis = kpis;
    this.syncKpiFavoritesSelection();
  }

  getSelectedKpis: () => Kpi[] = () => this.kpis.filter((kpi) => this.selectedKpis.includes(kpi.id));
  getDisplayableSelectedKpis: (displayable: boolean) => Kpi[] = (displayable: boolean) =>
    this.getSelectedKpis().filter((kpi) => {
      const granularity = this.periodService.granularity;
      return isKpiDisplayableForGranularity(kpi, granularity) === displayable;
    });
  indicatorsDate: string | null = null;
  overviewLastModified: Date | null = null;
  isBirdzAdmin = () => this.userService.isBirdzAdmin();
  overview: KpiOverviewItem[] = [];
  extraKpis = EXTRA_KPIS;
  getOverviewItem = (id: string) => this.overview.find((item: KpiOverviewItem) => item.id === id);
  kpiColors: Record<string, string> = {};
  // daily steps of YTD kpis: line color in the hourly view, and readable color for the tooltip texts
  kpiHourlyColors: Record<string, string> = {};
  kpiTextColors: Record<string, string> = {};
  getKpiLabel = (kpi: Kpi, inDropdown = false) =>
    kpi.label + (!inDropdown && kpi.daily_display && kpi.has_ytd_equivalent ? ` ${__('DAILY').toLowerCase()}` : '');
  // in the hourly view, kpis are suffixed with their periodicity (e.g. 'IP85 5min')
  getChartKpiLabel = (kpi: Kpi) =>
    this.periodService.granularity === 'hour' && kpi.hourly_period
      ? `${kpi.label} ${kpi.hourly_period}min`
      : this.getKpiLabel(kpi);
  // unavailable granularities, picker tabs and shortcuts are displayed disabled (with an explanation when possible)
  snapToValues: (TimeUnit | null)[] = [null, 'day', 'week', 'month', 'year'];
  disabledSnapToValues: DisabledGranularity[] = [];
  granularities: TimeUnit[] = ['hour', 'day', 'week', 'month', 'year'];
  disabledGranularities: DisabledGranularity[] = [];
  isGranularityDisabled = (granularity: TimeUnit) =>
    this.disabledGranularities.some(
      (disabled) => disabled === granularity || (typeof disabled === 'object' && disabled?.granularity === granularity)
    );
  undisplayableSelectedKpisMessage: string | null = null;
  shortcuts: TimeShortcut[] = [
    'yesterday',
    'the_day_before_yesterday',
    'this_week',
    'last_week',
    'this_month',
    'last_month',
    'this_year',
    'last_year',
  ];
  disabledShortcuts: TimeShortcut[] = [];
  kpi: string | null = null;
  chartOptions?: Highcharts.Options | null;
  Highcharts = Highcharts;
  updateFlag = false;
  isLoadingKpiRates = 0;
  kpiRates: KpiRateHistory | null = null;
  chart?: Highcharts.Chart;
  seriesVisibility = new Map<string, boolean>();
  autoscale: boolean = (sessionStorage.getItem('autoscale') ?? 'yes') === 'yes';
  isPlotLineSyncApplied = false;
  // the hourly view displays every slot of the period: limited for performance and readability (see GetKpiRatesRequest)
  readonly hourlyMaxDays = 2;
  isHourlyPeriodTooLong = false;

  constructor() {
    this.hasPerimeter$ = this.filterService.hasPerimeter$;
    this.techno$ = this.filterService.filter$.pipe(map((filter) => filter.techno));
  }

  ngOnInit(): void {
    this.fetchContractKpisAndOverview();
    this.fetchKpiRateHistory();
    this.syncDisabledShortcuts();
    this.selectHourlyGranularityForShortPeriods();
    this.applyPlotLineLegendHoverSync();
    this.listenToKpiFavorites();
  }

  listenToKpiFavorites() {
    this.favoritesService.favoriteApplied$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((kpis) => this.applyKpiFavorite(kpis))
      )
      .subscribe();
  }

  applyPlotLineLegendHoverSync() {
    // make sure we only apply this once
    if (this.isPlotLineSyncApplied) {
      return;
    }

    // wrap Highcharts' setState method to extend its functionality
    Highcharts.wrap(
      Highcharts.Series.prototype,
      'setState',
      function (this: Highcharts.Series, proceed: (this: Highcharts.Series, state: string) => void, state: string) {
        // let Highcharts proceed normally
        proceed.call(this, state);

        const seriesId = this.options.id;

        if (!seriesId) {
          return;
        }

        // find the series' associated plotline
        if (this.chart && this.chart.axes) {
          this.chart.axes.forEach((axis: HcAxisInternal) => {
            const plotLines = axis.plotLinesAndBands || [];

            // set the plotline opacity based on the series' state
            plotLines.forEach((plotLine) => {
              if (plotLine.options.id === seriesId) {
                // inactive means we are hovering over another series in which case we need to reduce the opacity of this plotline
                // note: other possible states are 'hover', 'select' (if allowPointSelect: true) and the default ''
                const targetOpacity = state === 'inactive' ? 0.2 : 1;

                if (plotLine.svgElem) {
                  plotLine.svgElem.animate({ opacity: targetOpacity }, { duration: 150 });
                }
                if (plotLine.label) {
                  plotLine.label.animate({ opacity: targetOpacity }, { duration: 150 });
                }
              }
            });
          });
        }
      }
    );

    this.isPlotLineSyncApplied = true;
  }

  /**
   * Sets default view depending on available granularities
   * If daily is available : 30 last days from yesterday
   * Else if weekly is available : 16 last weeks from yesterday's corresponding week
   * Else if monthly is available : 12 last months from yesterday's corresponding month
   */
  setDefaultView(): void {
    const yesterday = startOfYesterday();

    if (!this.isGranularityDisabled('day')) {
      const thirtyDaysPrior = sub(yesterday, { days: 29 }); // 29 + yesterday = 30
      this.periodService.updateState({
        snapTo: null,
        granularity: 'day',
        period: {
          from: thirtyDaysPrior,
          to: yesterday,
        },
      });
      return;
    }

    if (!this.isGranularityDisabled('week')) {
      const yesterdaysWeek = endOfISOWeek(yesterday);
      const sixteenWeeksPrior = startOfISOWeek(sub(yesterdaysWeek, { weeks: 15 })); // 15 + yesterday's week = 16
      this.periodService.updateState({
        snapTo: null,
        granularity: 'week',
        period: {
          from: sixteenWeeksPrior,
          to: yesterdaysWeek,
        },
      });
      return;
    }

    if (!this.isGranularityDisabled('month')) {
      const yesterdaysMonth = endOfMonth(yesterday);
      const twelveMonthsPrior = startOfMonth(sub(yesterday, { months: 11 })); // 11 + yesterday's month = 12
      this.periodService.updateState({
        snapTo: null,
        granularity: 'month',
        period: {
          from: twelveMonthsPrior,
          to: yesterdaysMonth,
        },
      });
      return;
    }
  }

  setChart(chart: Highcharts.Chart) {
    this.chart = chart;
  }

  toggleAutoscale(change: MatSlideToggleChange) {
    if (change.checked) {
      this.autoscale = true;
      sessionStorage.setItem('autoscale', 'yes');
    } else {
      this.autoscale = false;
      sessionStorage.setItem('autoscale', 'no');
    }
    this.updateChart();
  }

  handleLanguageChange() {
    this.transloco.langChanges$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.updateChart())
      )
      .subscribe();
  }

  /** see the `selectedKpis` setter, which is where this is called from on a selection change */
  syncKpiFavoritesSelection() {
    this.favoritesService.syncSelection(this.selectedKpis, this.availableKpiIds);
  }

  onKpiSelectionChanged() {
    this.updateGranularities();
    this.updateUndisplayableSelectedKpisMessage();
    this.updateChart();
  }

  /**
   * Applies a favorite : the current selection is overwritten by the favorite's one
   */
  applyKpiFavorite(kpis: string[]) {
    this.selectedKpis = kpis;
    this.onKpiSelectionChanged();
  }

  resetChart() {
    this.chartOptions = null;
  }

  getLocale(): Locale {
    const lang = this.transloco.getActiveLang();
    if (lang === 'fr') {
      return fr;
    }
    return enUS;
  }

  getXFormatter(): (x: string | number, separator?: string) => string {
    const locale = this.getLocale();
    const formatter = this.formatter;
    return (x: string | number) => {
      // because we set endOnTick to true, one last tick is forced to appear
      // but in some cases its value might be the tick index instead of the date
      // so if x is a number we use the last date from the selected period's data
      const dateString = typeof x !== 'number' ? x : (this.kpiRates?.points.at(-1)?.date ?? '');
      const date = parseISO(dateString);
      switch (this.kpiRates?.granularity) {
        case 'hour': {
          const time = format(date, 'HH:mm', { locale });
          return time === '00:00' ? formatter.date(dateString).replace(/\/?\d{4}\/?/, '') : time;
        }
        case 'month':
          return Str.capitalizeFirstLetter(format(date, 'MMMM', { locale }));
        case 'week':
          return __('birdz.week_shorthand') + format(date, 'ww', { locale });
        case 'day':
          const year = /\/?\d{4}\/?/;
          return formatter.date(dateString).replace(year, '');
        default:
          return '';
      }
    };
  }

  getEventsConfig(): Partial<Highcharts.PlotSeriesOptions> {
    const granularity = this.kpiRates?.granularity; // current displayed granularity
    // weekly and monthly points drill down to their days, daily points to their hourly slots (when available)
    const isHourlyAvailable = !this.isGranularityDisabled('hour');
    const drillDownGranularity =
      granularity === 'week' || granularity === 'month'
        ? 'day'
        : granularity === 'day' && isHourlyAvailable
          ? 'hour'
          : null;
    if (!granularity || !drillDownGranularity) {
      return {};
    }

    return {
      events: {
        click(e: Highcharts.SeriesClickEventObject) {
          // select the period corresponding to the clicked week, month or day (using date + snapTo)
          const date = e.point?.category;
          if (date) {
            document.dispatchEvent(
              new CustomEvent('period.goto', {
                detail: {
                  date: new Date(date),
                  snapTo: granularity,
                  granularity: drillDownGranularity,
                },
              })
            );
          }
        },
      },
      cursor: 'pointer',
    };
  }

  updateChart() {
    if (!this.kpiRates?.points) {
      return this.resetChart();
    }
    const xAxisCategories = this.kpiRates.points.map((p: KpiRateHistoryPoint) => p.date);
    const xFormatter = this.getXFormatter();
    const isHourly = this.kpiRates.granularity === 'hour';
    // the hourly view shows a single day: display it in the tooltip
    const tooltipDateFormatter = (x: string | number) =>
      isHourly && typeof x === 'string' ? `${this.formatter.date(x)} ${format(parseISO(x), 'HH:mm')}` : xFormatter(x);
    // one tick per hour and per displayed day (e.g. every 2 hours over 2 days)
    const hourlyPeriod = this.kpiRates.hourly_period ?? 0;
    const hourlyTickInterval = hourlyPeriod
      ? (60 / hourlyPeriod) * Math.ceil((xAxisCategories.length * hourlyPeriod) / (24 * 60))
      : undefined;
    const eventsConfig = this.getEventsConfig();
    const yFormatter = (y: number, type: KpiType) =>
      type === 'NB_JOURS'
        ? this.formatter.number(y, '1.0-0') + ` ${__('DAY_S').toLowerCase()}`
        : this.formatter.number(y, '1.2-2') + '%';

    const series: Highcharts.SeriesOptionsType[] = [];
    const labels = new Map<string, string>();
    let hasNbDaysSeries = false;

    // Arrays to hold initial plotlines for both Y-axes
    const plotLinesRateAxis: Highcharts.AxisPlotLinesOptions[] = [];
    const plotLinesNbDaysAxis: Highcharts.AxisPlotLinesOptions[] = [];

    this.getDisplayableSelectedKpis(true).forEach((kpi, index) => {
      const points = this.kpiRates?.points.map((point: KpiRateHistoryPoint) => point[kpi.id]);
      const isDailyStepOfYTD = !isHourly && kpi.daily_display && kpi.has_ytd_equivalent;
      const color = (isHourly && this.kpiHourlyColors[kpi.id]) || this.kpiColors[kpi.id];
      // no threshold for hourly kpis
      const thresholdSeriesName =
        !isHourly && !isDailyStepOfYTD && kpi.threshold !== null ? `${kpi.id}_threshold` : null;

      if (kpi.type === 'NB_JOURS') {
        hasNbDaysSeries = true;
      }

      // PlotLines configuration
      let plotLineConfig: Highcharts.AxisPlotLinesOptions | null = null;
      let thresholdIsVisible = true;

      if (thresholdSeriesName) {
        thresholdIsVisible =
          this.seriesVisibility.get(thresholdSeriesName) ?? this.seriesVisibility.get(kpi.id) ?? true;

        plotLineConfig = {
          id: thresholdSeriesName,
          value: kpi.threshold!,
          color: color,
          dashStyle: 'ShortDash',
          width: 2,
          zIndex: 10,
          label: {
            text: kpi.threshold + (kpi.type === 'NB_JOURS' ? __('DAY_SHORTHAND').toLowerCase() : '%'),
            align: kpi.type === 'NB_JOURS' ? 'right' : 'left',
            verticalAlign: 'bottom',
            x: kpi.type === 'NB_JOURS' ? -5 : 5,
            y: -5,
            style: {
              color: color,
              fontSize: '11px',
              fontWeight: '900',
            },
          },
        };

        // Add to initial rendering array if it should be visible
        if (thresholdIsVisible) {
          if (kpi.type === 'NB_JOURS') {
            plotLinesNbDaysAxis.push(plotLineConfig!);
          } else {
            plotLinesRateAxis.push(plotLineConfig!);
          }
        }
      }

      // Main KPI Series
      series.push({
        type: isDailyStepOfYTD ? 'column' : 'line',
        zIndex: isDailyStepOfYTD ? -1 : 1,
        yAxis: kpi.type === 'NB_JOURS' ? 1 : 0,
        dashStyle: kpi.admin_birdz_display ? 'ShortDot' : 'Solid',
        id: kpi.id,
        name: kpi.id,
        index: index,
        color: color,
        data: points?.every((y) => y === null) ? [] : points, // we provide [] to display the "noData" message
        custom: {
          type: kpi.type,
          // tooltip text: the light daily step colors are not readable, use their dark variant
          color: (isDailyStepOfYTD && this.kpiTextColors[kpi.id]) || color,
          isBirdzAdmin: kpi.admin_birdz_display,
        },
        visible: this.seriesVisibility.get(kpi.id) ?? true,
        events: {
          legendItemClick: (e: Highcharts.SeriesLegendItemClickEventObject) => {
            const currentSeries = e.target;

            // setTimeout to wait for the native toggle to complete before checking state
            setTimeout(() => {
              const isVisible = currentSeries.visible;
              this.seriesVisibility.set(currentSeries.name, isVisible);

              // Handle linking the PlotLine visibility
              if (thresholdSeriesName && plotLineConfig) {
                const thresholdSeries = currentSeries.chart.series.find((s) => s.name === thresholdSeriesName);

                if (thresholdSeries) {
                  // Update the dummy series legend state to match the main KPI
                  thresholdSeries.setVisible(isVisible);
                  this.seriesVisibility.set(thresholdSeriesName, isVisible);

                  // Add or Remove the actual PlotLine using direct axis reference
                  const axis = currentSeries.yAxis;

                  if (isVisible) {
                    axis.removePlotLine(thresholdSeriesName); // Remove first to prevent duplicates
                    axis.addPlotLine(plotLineConfig);
                  } else {
                    axis.removePlotLine(thresholdSeriesName);
                  }
                }
              }
            }, 0);
          },
        },
      });

      labels.set(kpi.id, this.getChartKpiLabel(kpi));

      // Dummy Threshold Series (for the legend)
      if (thresholdSeriesName && plotLineConfig) {
        series.push({
          type: 'line',
          dashStyle: 'ShortDash',
          id: thresholdSeriesName,
          name: thresholdSeriesName,
          index: index,
          color: color,
          data: [kpi.threshold],
          marker: { enabled: false },
          enableMouseTracking: false,
          showInLegend: true,
          visible: thresholdIsVisible,
          yAxis: kpi.type === 'NB_JOURS' ? 1 : 0,
          custom: {
            isBirdzAdmin: kpi.admin_birdz_display,
          },
          events: {
            legendItemClick: (e: Highcharts.SeriesLegendItemClickEventObject) => {
              const dummySeries = e.target;

              // setTimeout to wait for the native toggle to complete before checking state
              setTimeout(() => {
                const isVisible = dummySeries.visible;
                this.seriesVisibility.set(dummySeries.name, isVisible);

                // Toggle the linked PlotLine
                const axis = dummySeries.yAxis;

                if (isVisible) {
                  axis.removePlotLine(thresholdSeriesName); // Remove first to prevent duplicates
                  axis.addPlotLine(plotLineConfig!);
                } else {
                  axis.removePlotLine(thresholdSeriesName);
                }
              }, 0);
            },
          },
        });

        labels.set(thresholdSeriesName, translate('KPI_THRESHOLD', { kpi: this.getKpiLabel(kpi) }));
      }
    });

    this.chartOptions = {
      credits: {
        enabled: false,
      },
      title: {
        text: undefined,
      },
      chart: {
        plotBackgroundColor: undefined,
        plotBorderWidth: undefined,
        plotShadow: false,
        type: 'line',
        style: {
          fontFamily: fontFamily,
        },
      },
      tooltip: {
        useHTML: true,
        formatter: function (this: Highcharts.Point) {
          const tooltip = (this.points ?? []).reduce(
            (s: string, point: Highcharts.Point) => {
              const isBirdzAdmin = point.series.options.custom && point.series.options.custom['isBirdzAdmin'];
              const type = point.series.options.custom && point.series.options.custom['type'];
              const color = (point.series.options.custom && point.series.options.custom['color']) ?? point.color;

              return `${s}
                <span class="kpi ${isBirdzAdmin ? 'birdz-admin' : ''}">${labels.get(point.series.name)}${__('COLON')} </span>
                <span class="rate" style="color: ${color}">${point.y != null ? yFormatter(point.y, type) : '-'}</span>`;
            },
            `<span class="date">${tooltipDateFormatter(this.category ?? '')}</span>`
          );

          return `<div class="kpi-rate-tooltip">${tooltip}</div>`;
        },
        padding: 0,
        className: 'kpi-rate-tooltip-wrapper',
        shared: true,
      },
      legend: {
        useHTML: true,
        symbolRadius: 0,
        labelFormatter: function (this: Highcharts.Series | Highcharts.Point) {
          const isBirdzAdmin = this.options.custom?.['isBirdzAdmin'];
          return `<div class="legend__title ${isBirdzAdmin ? 'birdz-admin' : ''}">${labels.get(this.name ?? '')}</div>`;
        },
      },
      xAxis: {
        showEmpty: true,
        endOnTick: true,
        categories: xAxisCategories,
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            return xFormatter(this.value, '<br>');
          },
        },
        tickInterval: isHourly ? hourlyTickInterval : undefined,
        min: 0, // forces the xAxis'ticks to de displayed when there is no data
        max: xAxisCategories.length - 1, // forces the xAxis'ticks to de displayed when there is no data
      },
      lang: {
        noData: __('NO_DATA_AVAILBLE_FOR_THIS_PERIOD'),
      },
      noData: {
        style: {
          fontWeight: 'normal',
          fontSize: '16px',
          color: '#00000099',
        },
      },
      yAxis: [
        {
          title: {
            text: __('RATE'),
          },
          // the autosize option only applies to watermetering
          min: this.filterService.getTechno() === 'watermetering' && this.autoscale ? undefined : 0,
          max: this.filterService.getTechno() === 'watermetering' && this.autoscale ? undefined : 100,
          // keeps the autoscale padding within a valid rate range
          floor: 0,
          ceiling: 100,
          minRange: 2,
          labels: {
            format: '{value:.2f} %',
          },
          showEmpty: false,
          plotLines: plotLinesRateAxis,
        },
        hasNbDaysSeries
          ? {
              title: {
                text: __('NB_DAYS'),
              },
              opposite: true,
              showEmpty: false,
              allowDecimals: false,
              plotLines: plotLinesNbDaysAxis,
            }
          : undefined,
      ].filter((axis) => axis !== undefined),
      plotOptions: {
        series: {
          animation: false,
          marker: {
            symbol: 'circle',
          },
          lineWidth: 2,
          connectNulls: true,
          ...eventsConfig,
        },
        column: {
          pointPadding: 0,
          groupPadding: 0,
          shadow: false,
        },
      },
      series: series,
    };

    this.updateFlag = true;
  }

  updateGranularities() {
    // disable granularities for which there are no displayed KPIs
    this.disabledGranularities = this.granularities
      .filter((granularity) => {
        return this.getSelectedKpis().every((kpi) => !isKpiDisplayableForGranularity(kpi, granularity));
      })
      .map((granularity) => {
        const tooltip = __('NONE_OF_SELECTED_KPIS_ARE_DISPLAYABLE.' + granularity.toUpperCase());
        return { granularity, tooltip };
      });

    // single day selections (picker tab and shortcuts) go with the hourly view
    this.disabledSnapToValues = this.isGranularityDisabled('hour')
      ? [{ granularity: 'day', tooltip: __('NONE_OF_SELECTED_KPIS_ARE_DISPLAYABLE.HOUR') }]
      : [];
    this.updateDisabledShortcuts();
  }

  updateDisabledShortcuts() {
    this.disabledShortcuts = [
      ...(this.periodService.granularity === 'month' ? (['last_week', 'this_week'] as TimeShortcut[]) : []),
      ...(this.isGranularityDisabled('hour') ? (['yesterday', 'the_day_before_yesterday'] as TimeShortcut[]) : []),
    ];
  }

  selectAvailabledGranularity() {
    // select the first available granularity if the current one is disabled
    if (this.isGranularityDisabled(this.periodService.granularity)) {
      const availableGranularities = this.granularities.filter(
        (granularity) => !this.isGranularityDisabled(granularity)
      );
      this.periodService.granularity =
        availableGranularities.find((granularity) => granularity !== 'hour') ?? availableGranularities.at(0) ?? 'day';
    }
  }

  updateUndisplayableSelectedKpisMessage() {
    const undisplayableSelectedKpis = this.getDisplayableSelectedKpis(false).map((kpi) => this.getKpiLabel(kpi));

    if (undisplayableSelectedKpis.length > 0) {
      const granularity = this.periodService.granularity;
      const warning = __('NOT_ALL_SELECTED_KPIS_ARE_DISPLAYABLE.' + granularity.toUpperCase());
      const kpis = undisplayableSelectedKpis.join(', ');
      this.undisplayableSelectedKpisMessage = `${warning} (${kpis}).`;
    } else {
      this.undisplayableSelectedKpisMessage = null;
    }
  }

  updateKpisAndColors(kpis: Kpi[]) {
    const showRawKpis = this.userService.getShowRawKpis();
    this.kpis = kpis
      .filter((kpi) => !kpi.admin_birdz_display || this.userService.isBirdzAdmin())
      .filter((kpi) => !kpi.raw_display || showRawKpis)
      .sort(Str.sortBy('label'));
    this.availableKpiIds = this.kpis.map((kpi) => kpi.id);
    // the other half of what the favorites need, the selection being reported by its setter
    this.syncKpiFavoritesSelection();
    const scale = chroma.scale([blueZodiac, orange]).mode('lch').colors(this.kpis.length);
    const colors: Record<string, string> = {};
    const hourlyColors: Record<string, string> = {};
    const textColors: Record<string, string> = {};
    let dailyStepIndex = 0;
    this.kpis.forEach((kpi, index) => {
      const base = scale.at(index) ?? '#000000';
      if (kpi.daily_display && kpi.has_ytd_equivalent) {
        // daily steps of YTD kpis are columns: one distinct softened color each, darkened for the tooltip texts
        const stepColor = DAILY_STEP_COLORS[dailyStepIndex++ % DAILY_STEP_COLORS.length];
        colors[kpi.id] = chroma.mix(stepColor, 'white', 0.6).hex();
        textColors[kpi.id] = chroma(stepColor).darken(1.5).hex();
        // in the hourly view they are drawn as lines, like any other kpi
        hourlyColors[kpi.id] = base;
      } else {
        colors[kpi.id] = kpi.raw_display ? chroma(base).brighten(1.5).desaturate(0.5).hex() : base;
      }
    });
    this.kpiColors = colors;
    this.kpiHourlyColors = hourlyColors;
    this.kpiTextColors = textColors;
  }

  fetchContractKpisAndOverview() {
    combineLatest([this.filterService.filter$, this.transloco.langChanges$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        this.routing.pauseWhileDetached(this.ref, this.destroyRef),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        tap(() => this.updateKpisAndColors([])),
        tap(() => (this.selectedKpis = [])),
        tap(() => (this.overview = [])),
        tap(() => (this.indicatorsDate = null)),
        tap(() => (this.overviewLastModified = null)),
        filter(() => this.filterService.getFilter().contract !== null),
        tap(() => this.isLoading++),
        switchMap(() =>
          this.api
            .fetchContractKpis(
              this.filterService.getContract() as string,
              this.filterService.getTechno() as string,
              false
            )
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              tap((kpis) => {
                this.updateKpisAndColors(kpis);
                // on sélectionne tous les KPIs (sauf bruts) par défaut
                this.selectedKpis = kpis.filter((kpi) => !kpi.raw_display).map((kpi) => kpi.id);
                this.updateGranularities();
                this.setDefaultView();
                this.updateUndisplayableSelectedKpisMessage();
              }),
              filter((kpis) => kpis.length > 0),
              this.messenger.showFriendlyErrorMessage(EMPTY),
              finalize(() => this.isLoading--)
            )
        ),
        tap(() => this.isLoading++),
        switchMap(() =>
          this.api.fetchKpiOverview(this.filterService.toParams()).pipe(
            takeUntilDestroyed(this.destroyRef),
            tap((overview) => (this.overview = overview.data)),
            tap((overview) => (this.indicatorsDate = overview.indicators_date)),
            tap((overview) => (this.overviewLastModified = overview.last_modified)),
            this.messenger.showFriendlyErrorMessage(EMPTY),
            finalize(() => this.isLoading--)
          )
        )
      )
      .subscribe();
  }

  fetchKpiRateHistory() {
    combineLatest([
      this.filterService.filter$,
      this.periodService.period$,
      this.periodService.granularity$,
      this.transloco.langChanges$,
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        this.routing.pauseWhileDetached(this.ref, this.destroyRef),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        delay(1),
        filter(([, period]) => period !== null && this.filterService.hasPerimeter()),
        tap(() => (this.kpiRates = null)),
        tap(() => this.isLoadingKpiRates++),
        tap(() => this.selectAvailabledGranularity()),
        tap(([, period, granularity]) => {
          this.isHourlyPeriodTooLong =
            granularity === 'hour' && this.countPeriodDays(period as Period) > this.hourlyMaxDays;
        }),
        switchMap(([, period, granularity]) => {
          // beyond the hourly limit, nothing is fetched and a message asks for a shorter period
          const kpiRates$: Observable<KpiRateHistory> = this.isHourlyPeriodTooLong
            ? EMPTY
            : this.api.fetchKpiRateHistory(this.filterService.toParams(), period as Period, granularity as TimeUnit);
          return kpiRates$.pipe(
            takeUntilDestroyed(this.destroyRef),
            this.messenger.showFriendlyErrorMessage(EMPTY),
            tap((kpiRates) => (this.kpiRates = kpiRates)),
            tap(() => {
              this.updateUndisplayableSelectedKpisMessage();
            }),
            finalize(() => {
              this.updateChart();
              this.isLoadingKpiRates--;
            })
          );
        })
      )
      .subscribe();
  }

  countPeriodDays(period: Period): number {
    return differenceInCalendarDays(period.to as Date, period.from as Date) + 1;
  }

  /**
   * Short periods (up to the hourly view limit, e.g. a single day) switch to the hourly view when available.
   * Only on period changes: the user can still go back to another granularity for the same period.
   */
  selectHourlyGranularityForShortPeriods() {
    this.periodService.period$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        this.routing.pauseWhileDetached(this.ref, this.destroyRef),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        filter((period) => period !== null && this.countPeriodDays(period) <= this.hourlyMaxDays),
        filter(() => !this.isGranularityDisabled('hour')),
        // wait for the period state to be fully propagated: updating the state while it is being emitted
        // would let the subscribers notified afterwards (e.g. the granularity toggle) receive the old granularity last
        delay(0),
        tap(() => (this.periodService.granularity = 'hour'))
      )
      .subscribe();
  }

  syncDisabledShortcuts() {
    this.periodService.granularity$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.updateDisabledShortcuts())
      )
      .subscribe();
  }

  openContractKpiHelp() {
    this.dialog.open(ModalHelpContractConfigComponent, {
      maxWidth: '900px',
      data: {
        kpis: this.kpis,
        techno: this.filterService.getTechno(),
        overview: this.overview,
      },
    });
  }

  openChartHelp() {
    if (this.filterService.getTechno() === 'watermetering') {
      this.dialog.open(ModalHelpIndexReportingComponent, { maxWidth: '900px' });
      return;
    }

    const kpis: { type: string; label: string; color: string; desc: string }[] = [];
    this.getDisplayableSelectedKpis(true).forEach((kpi) => {
      kpis.push({
        type: kpi.daily_display && kpi.has_ytd_equivalent ? 'column' : 'line',
        label: this.getKpiLabel(kpi),
        color: this.kpiColors[kpi.id],
        desc: __(kpi.id + '_DESC') !== kpi.id + '_DESC' ? __(kpi.id + '_DESC') : '',
      });
    });

    this.dialog.open(ModalHelpSewerinDataReportingComponent, { width: '900px', maxWidth: '900px', data: { kpis } });
  }

  export() {
    const kpis = this.getDisplayableSelectedKpis(true);

    if (!this.kpiRates?.points || kpis.length === 0) {
      this.messenger.error(__('NO_DATA_TO_EXPORT'));
      return;
    }

    const dateFormatter = (date: string) => {
      const locale = this.getLocale();
      switch (this.kpiRates!.granularity) {
        case 'hour':
          return `${this.formatter.date(date)} ${format(date, 'HH:mm', { locale })}`;
        case 'month':
          return Str.capitalizeFirstLetter(format(date, 'MMMM-yyyy', { locale }));
        case 'week':
          return __('birdz.week_shorthand') + format(date, 'ww-yyyy', { locale });
        case 'day':
          return this.formatter.date(date);
        default:
          return '';
      }
    };

    const data: (string | number | null)[][] = [];
    data.push([__('DATE'), ...kpis.map((kpi) => this.getChartKpiLabel(kpi))]);

    this.kpiRates.points.forEach((point) => {
      const date = dateFormatter(point.date);
      data.push([
        date,
        ...kpis.map((kpi) => (point[kpi.id] != null ? Math.round((point[kpi.id] as number) * 100) / 100 : null)),
      ]);
    });

    const datetime = format(new Date(), 'yyyy_MM_dd_HH_mm_ss');
    const id = randomString(10);
    const techno = this.filterService.getTechno();
    const technoName = techno === 'watermetering' ? 'watermeter' : techno;
    const filename = `historicized_rate_of_index_reporting_${technoName}_${datetime}_${id}`;
    const separator = this.formatter.config.csvSeparator ?? ';';
    this.csvExport.downloadFile(data, filename, separator);
  }

  getDeviation(value: number | null, threshold: number | null) {
    if (value === null || threshold === null) {
      return null;
    }

    const deviation = value - threshold;

    return {
      value: deviation,
      sign: deviation >= 0 ? '+' : '',
      color: deviation >= 0 ? green : red,
    };
  }
}
